import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { employeeMenuItems } from '../components/employee/employeeMenu';
import { managerMenuItems } from '../components/manager/managerMenu';
import { getCompanyEmployees, getTrips } from '../services/dashboardService';
import {
  CreateExpenseEventPayload,
  UpiIntentStatusRecord,
  SplitStrategy,
  createUpiIntent,
  createExpenseEvent,
  getUpiIntentStatus,
  getExpenseEvents,
  isExpenseApiError,
  mockConfirmUpiIntent,
  uploadExpenseReceipt,
} from '../services/expenseService';

type EmployeeOption = {
  id: string;
  name: string;
};

type ParsedBillDraft = {
  id: string;
  filename: string;
  category: string;
  merchant: string;
  amount: number;
  confidence: 'high' | 'medium' | 'low';
  source: 'filename' | 'content' | 'hybrid' | 'ocr' | 'pdf_text';
};

type PaymentLoopState =
  | { step: 'idle' }
  | { step: 'pending'; intentId: string; transactionRef: string; startedAt: number; message: string }
  | { step: 'success'; intent: UpiIntentStatusRecord; message: string }
  | { step: 'failed'; intentId: string; message: string };

const strategies: SplitStrategy[] = ['EQUAL', 'BY_HEADCOUNT', 'PERCENTAGE', 'EXACT'];
const formatINR = (amount: number): string => `₹${Math.round(amount).toLocaleString('en-IN')}`;

const categoryKeywords: Array<{ category: string; terms: string[] }> = [
  { category: 'Hotel', terms: ['hotel', 'stay', 'room', 'resort', 'inn'] },
  { category: 'Meals', terms: ['meal', 'food', 'dinner', 'lunch', 'breakfast', 'restaurant', 'cafe'] },
  { category: 'Cab', terms: ['cab', 'taxi', 'uber', 'ola', 'ride', 'auto'] },
  { category: 'Flight', terms: ['flight', 'airline', 'boarding'] },
  { category: 'Train', terms: ['train', 'rail'] },
  { category: 'Fuel', terms: ['fuel', 'petrol', 'diesel'] },
  { category: 'Parking', terms: ['parking', 'toll'] },
  { category: 'Office Supplies', terms: ['stationery', 'supplies', 'office'] },
];

const toMerchantId = (value: string): string | undefined => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return normalized || undefined;
};

const amountRegex = /(?:rs\.?|inr|₹)?\s?(\d{1,3}(?:[,\s]\d{2,3})*(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)/gi;

const normalizeCurrencyValue = (value: string): number => {
  const cleaned = value.replace(/[,\s]/g, '').trim();
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

const detectCategory = (input: string): string => {
  const lower = input.toLowerCase();
  for (const row of categoryKeywords) {
    if (row.terms.some((term) => lower.includes(term))) return row.category;
  }
  return 'Other';
};

const extractAmount = (input: string): number => {
  const matches = Array.from(input.matchAll(amountRegex))
    .map((item) => normalizeCurrencyValue(item[1] || '0'))
    .filter((value) => value > 0);
  if (matches.length === 0) return 0;
  return Math.max(...matches);
};

const inferMerchantFromFilename = (filename: string): string => {
  const base = filename.replace(/\.[^/.]+$/, '');
  const words = base
    .replace(/[_-]+/g, ' ')
    .replace(/\d+/g, ' ')
    .split(' ')
    .map((word) => word.trim())
    .filter((word) => word.length >= 3);
  if (words.length === 0) return 'Unknown merchant';
  return words.slice(0, 3).join(' ');
};

const isImageFile = (file: File): boolean =>
  file.type.startsWith('image/') ||
  /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(file.name);

const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

const extractTextFromImageOcr = async (file: File): Promise<string> => {
  const tesseractModule = await import('tesseract.js');
  const result = await tesseractModule.recognize(file, 'eng');
  return String(result?.data?.text || '').trim();
};

const extractTextFromPdf = async (file: File): Promise<string> => {
  const pdfModule = await import('pdfjs-dist/legacy/build/pdf');
  const buffer = await file.arrayBuffer();
  const loadingTask = pdfModule.getDocument({
    data: buffer,
    disableWorker: true,
  });
  const pdf = await loadingTask.promise;
  const pages = Math.min(pdf.numPages, 3);
  let text = '';

  for (let pageNo = 1; pageNo <= pages; pageNo += 1) {
    const page = await pdf.getPage(pageNo);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item) => ('str' in item ? String(item.str) : ''))
      .join(' ');
    text += ` ${pageText}`;
  }
  return text.trim();
};

const parseBillFile = async (file: File): Promise<ParsedBillDraft> => {
  const filenameText = file.name || '';
  let contentText = '';
  let source: ParsedBillDraft['source'] = 'filename';

  if (file.type.startsWith('text/') || file.name.toLowerCase().endsWith('.csv')) {
    try {
      contentText = await file.text();
      source = 'content';
    } catch (_) {
      contentText = '';
    }
  }
  if (!contentText && isImageFile(file)) {
    try {
      contentText = await extractTextFromImageOcr(file);
      if (contentText) source = 'ocr';
    } catch (_) {
      contentText = '';
    }
  }
  if (!contentText && isPdfFile(file)) {
    try {
      contentText = await extractTextFromPdf(file);
      if (contentText) source = 'pdf_text';
    } catch (_) {
      contentText = '';
    }
  }

  const amountFromContent = extractAmount(contentText);
  const amountFromFilename = extractAmount(filenameText);
  const amount = amountFromContent || amountFromFilename;
  const categoryFromContent = detectCategory(contentText);
  const categoryFromFilename = detectCategory(filenameText);
  const category = categoryFromContent !== 'Other' ? categoryFromContent : categoryFromFilename;
  const merchant = inferMerchantFromFilename(filenameText);
  if (contentText && source === 'content' && amountFromContent > 0 && amountFromFilename > 0) {
    source = 'hybrid';
  }
  const confidence: ParsedBillDraft['confidence'] = amount > 0 && category !== 'Other'
    ? 'high'
    : amount > 0 || category !== 'Other'
      ? 'medium'
      : 'low';

  return {
    id: `${file.name}-${file.lastModified}-${file.size}`,
    filename: file.name,
    category,
    merchant,
    amount,
    confidence,
    source,
  };
};

const toBase64WithoutPrefix = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const CreateExpenseEventPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isManagerView = location.pathname.startsWith('/manager/');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isParsingBills, setIsParsingBills] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billUploadInfo, setBillUploadInfo] = useState<string | null>(null);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [tripOptions, setTripOptions] = useState<Array<{ id: string; label: string; destination: string; period: string; budgetCap: number }>>([]);
  const [spentByTripId, setSpentByTripId] = useState<Record<string, number>>({});
  const [parsedBills, setParsedBills] = useState<ParsedBillDraft[]>([]);

  const [tripId, setTripId] = useState('');
  const [category, setCategory] = useState('Hotel');
  const [merchant, setMerchant] = useState('');
  const [notes, setNotes] = useState('');
  const [totalAmount, setTotalAmount] = useState('0');
  const [splitStrategy, setSplitStrategy] = useState<SplitStrategy>('EQUAL');
  const [paidByEmployeeId, setPaidByEmployeeId] = useState('');

  const [splitRows, setSplitRows] = useState<Array<{ employeeId: string; amount: string; pct: string }>>([
    { employeeId: '', amount: '0', pct: '0' },
  ]);
  const [claimGst, setClaimGst] = useState(false);
  const [vendorGstin, setVendorGstin] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [taxableAmount, setTaxableAmount] = useState('0');
  const [cgstAmount, setCgstAmount] = useState('0');
  const [sgstAmount, setSgstAmount] = useState('0');
  const [igstAmount, setIgstAmount] = useState('0');
  const [showGstDetails, setShowGstDetails] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptUiState, setReceiptUiState] = useState<'idle' | 'uploaded' | 'processing' | 'parsed' | 'failed'>('idle');
  const [receiptHint, setReceiptHint] = useState<string>('Add bill (optional for faster approval).');
  const [paymentLoop, setPaymentLoop] = useState<PaymentLoopState>({ step: 'idle' });

  useEffect(() => {
    const load = async () => {
      const [trips, employeeRows, events] = await Promise.all([getTrips({ perPage: 300 }), getCompanyEmployees(), getExpenseEvents({ perPage: 500 })]);
      const mappedTrips = trips.map((trip) => ({
        id: trip.id,
        label: `${trip.destination || 'Trip'} (${trip.id.slice(0, 8)})`,
        destination: trip.destination || 'Business Trip',
        period:
          trip.start_date && trip.end_date
            ? `${new Date(trip.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(trip.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : 'Dates not set',
        budgetCap: Number(trip.estimated_budget || 0),
      }));
      const spentMap: Record<string, number> = {};
      events.forEach((event) => {
        const eventTripId = String(event.trip_id || '');
        if (!eventTripId) return;
        const splitSum = (event.splits || []).reduce((sum, split) => sum + Number(split.split_amount || 0), 0);
        spentMap[eventTripId] = (spentMap[eventTripId] || 0) + splitSum;
      });
      const mappedEmployees = employeeRows.map((row) => ({ id: row.id, name: row.name || row.email }));
      setTripOptions(mappedTrips);
      setSpentByTripId(spentMap);
      setEmployees(mappedEmployees);
      setTripId(mappedTrips[0]?.id || '');
      setPaidByEmployeeId(mappedEmployees[0]?.id || '');
      setSplitRows((prev) => prev.map((row, idx) => ({ ...row, employeeId: row.employeeId || mappedEmployees[idx]?.id || mappedEmployees[0]?.id || '' })));
    };

    load().catch(() => {
      setError('Failed to load trips/employees.');
    });
  }, []);

  const parsedTotal = useMemo(() => Number(totalAmount || 0), [totalAmount]);
  const selectedTrip = useMemo(() => tripOptions.find((trip) => trip.id === tripId) || null, [tripOptions, tripId]);
  const selectedTripSpent = selectedTrip ? Number(spentByTripId[selectedTrip.id] || 0) : 0;
  const selectedTripBudget = selectedTrip ? selectedTrip.budgetCap : 0;
  const selectedTripRemaining = selectedTripBudget - selectedTripSpent;
  const selectedTripBudgetPct = selectedTripBudget > 0
    ? Math.min(100, Math.round((selectedTripSpent / selectedTripBudget) * 100))
    : 0;
  const parsedBillsTotal = parsedBills.reduce((sum, row) => sum + row.amount, 0);
  const isHighValueExpense = parsedTotal >= 15000;
  const gstTotal = Number(cgstAmount || 0) + Number(sgstAmount || 0) + Number(igstAmount || 0);

  useEffect(() => {
    if (claimGst) {
      setShowGstDetails(true);
      setReceiptHint('Required for GST claim.');
      return;
    }
    if (parsedBills.length > 0 || isHighValueExpense) {
      setShowGstDetails(true);
    }
    if (isHighValueExpense) {
      setReceiptHint('Needed due to policy: high-value expense.');
    } else {
      setReceiptHint('Add bill (optional for faster approval).');
    }
  }, [claimGst, parsedBills.length, isHighValueExpense]);

  const applyParsedBill = (bill: ParsedBillDraft) => {
    if (bill.category && bill.category !== 'Other') {
      setCategory(bill.category);
    }
    if (bill.merchant && bill.merchant !== 'Unknown merchant') {
      setMerchant(bill.merchant);
    }
    if (bill.amount > 0) {
      setTotalAmount(String(Math.round(bill.amount)));
    }
    setNotes((current) => {
      const prefix = current.trim();
      const fragment = `Parsed from ${bill.filename}`;
      if (!prefix) return fragment;
      if (prefix.includes(fragment)) return prefix;
      return `${prefix}\n${fragment}`;
    });
  };

  const applyAllParsedBills = () => {
    if (parsedBills.length === 0) return;
    const topCategory = parsedBills
      .filter((row) => row.category !== 'Other')
      .reduce<Record<string, number>>((acc, row) => {
        acc[row.category] = (acc[row.category] || 0) + 1;
        return acc;
      }, {});
    const dominantCategory = Object.entries(topCategory)
      .sort((a, b) => b[1] - a[1])[0]?.[0];
    if (dominantCategory) setCategory(dominantCategory);
    if (parsedBillsTotal > 0) setTotalAmount(String(Math.round(parsedBillsTotal)));
    setNotes((current) => {
      const prefix = current.trim();
      const fileList = parsedBills.map((row) => row.filename).join(', ');
      const fragment = `Bulk parsed bills: ${fileList}`;
      if (!prefix) return fragment;
      if (prefix.includes('Bulk parsed bills:')) return prefix;
      return `${prefix}\n${fragment}`;
    });
  };

  const onBulkBillUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      setParsedBills([]);
      setBillUploadInfo(null);
      return;
    }
    setIsParsingBills(true);
    setBillUploadInfo(null);
    try {
      const drafts = await Promise.all(files.map((file) => parseBillFile(file)));
      setParsedBills(drafts);
      const highConfidence = drafts.filter((row) => row.confidence === 'high').length;
      setBillUploadInfo(`Parsed ${drafts.length} bill(s). ${highConfidence} high-confidence draft(s).`);
    } catch (_) {
      setBillUploadInfo('Could not auto-parse one or more bills. You can still fill details manually.');
      setParsedBills([]);
    } finally {
      setIsParsingBills(false);
      event.target.value = '';
    }
  };

  const attachReceiptIfPresent = async (expenseEventId: string) => {
    if (!receiptFile) return;
    setReceiptUiState('processing');
    try {
      const base64 = await toBase64WithoutPrefix(receiptFile);
      const topParsed = parsedBills[0];
      await uploadExpenseReceipt(expenseEventId, {
        file_name: receiptFile.name,
        content_type: receiptFile.type || 'application/octet-stream',
        data_base64: base64,
        receipt_metadata: topParsed
          ? {
              extracted_amount: topParsed.amount || undefined,
              extracted_vendor: topParsed.merchant || undefined,
              extracted_date: undefined,
              gstin_detected: claimGst ? vendorGstin || undefined : undefined,
              confidence_score:
                topParsed.confidence === 'high'
                  ? 0.9
                  : topParsed.confidence === 'medium'
                    ? 0.65
                    : 0.35,
            }
          : undefined,
      });
      setReceiptUiState('parsed');
    } catch (_) {
      setReceiptUiState('failed');
    }
  };

  const pollIntentStatus = async (intentId: string): Promise<UpiIntentStatusRecord> => {
    const status = await getUpiIntentStatus(intentId);
    if (status.status === 'CONFIRMED' || status.status === 'FAILED') return status;
    throw new Error('still_pending');
  };

  const onPayViaUpi = async () => {
    setError(null);
    if (!tripId) {
      setError('Select a trip before paying.');
      return;
    }
    if (!category.trim()) {
      setError('Category is required.');
      return;
    }
    if (!Number.isFinite(parsedTotal) || parsedTotal <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    if (claimGst && !receiptFile) {
      setError('Receipt is required when GST is claimed.');
      return;
    }
    if (!window.confirm(`Proceed to UPI payment of ${formatINR(parsedTotal)} for ${category}?`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const intent = await createUpiIntent({
        trip_id: tripId,
        category,
        amount: parsedTotal,
        notes: notes || `${category} for ${selectedTrip?.destination || 'trip'}`,
        payment_method: 'UPI_WEB',
        merchant_name: merchant || undefined,
        merchant_id: toMerchantId(merchant),
        expense_at: new Date().toISOString(),
      });
      setPaymentLoop({
        step: 'pending',
        intentId: intent.intent_id,
        transactionRef: intent.transaction_ref,
        startedAt: Date.now(),
        message: 'Waiting for payment confirmation…',
      });
      if (intent.intent_url) {
        try {
          window.location.href = intent.intent_url;
        } catch (_) {
          // User may continue from pending card actions.
        }
      }
    } catch (submitError) {
      if (isExpenseApiError(submitError)) setError(submitError.message);
      else if (submitError instanceof Error) setError(submitError.message);
      else setError('Failed to initiate UPI payment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCheckPaymentStatus = async () => {
    if (paymentLoop.step !== 'pending') return;
    try {
      const status = await pollIntentStatus(paymentLoop.intentId);
      if (status.status === 'CONFIRMED') {
        if (status.expense_event_id) {
          await attachReceiptIfPresent(status.expense_event_id);
        }
        setPaymentLoop({
          step: 'success',
          intent: status,
          message: 'Payment successful. Expense created.',
        });
      } else {
        setPaymentLoop({
          step: 'failed',
          intentId: paymentLoop.intentId,
          message: status.failure_reason || 'Payment failed.',
        });
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'still_pending') {
        setPaymentLoop((prev) =>
          prev.step === 'pending'
            ? { ...prev, message: 'Still processing webhook confirmation…' }
            : prev,
        );
        return;
      }
      setPaymentLoop({
        step: 'failed',
        intentId: paymentLoop.intentId,
        message: 'Could not fetch payment status.',
      });
    }
  };

  const onSimulateWebhook = async () => {
    if (paymentLoop.step !== 'pending') return;
    try {
      const mock = await mockConfirmUpiIntent(paymentLoop.intentId, true);
      const status = mock.intent;
      if (status.expense_event_id) {
        await attachReceiptIfPresent(status.expense_event_id);
      }
      setPaymentLoop({
        step: 'success',
        intent: status,
        message: 'Payment confirmed via webhook and expense created.',
      });
    } catch (err) {
      if (isExpenseApiError(err)) {
        setPaymentLoop({
          step: 'failed',
          intentId: paymentLoop.intentId,
          message: err.message,
        });
      } else {
        setPaymentLoop({
          step: 'failed',
          intentId: paymentLoop.intentId,
          message: 'Failed to simulate payment confirmation.',
        });
      }
    }
  };

  useEffect(() => {
    if (paymentLoop.step !== 'pending') return undefined;
    const timer = window.setInterval(() => {
      onCheckPaymentStatus().catch(() => undefined);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [paymentLoop.step, paymentLoop.step === 'pending' ? paymentLoop.intentId : '']);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const payload: CreateExpenseEventPayload = {
        trip_id: tripId,
        category,
        merchant,
        notes,
        total_amount: parsedTotal,
        split_strategy: splitStrategy,
        payments: [{
          paid_by_employee_id: paidByEmployeeId,
          amount_paid: parsedTotal,
          payment_method: 'manual',
        }],
        splits: splitRows
          .filter((row) => row.employeeId)
          .map((row) => ({
            beneficiary_employee_id: row.employeeId,
            split_amount: Number(row.amount || 0),
            split_pct: Number(row.pct || 0),
          })),
        gst: {
          is_gst_applicable: claimGst,
          vendor_gstin: vendorGstin,
          invoice_number: invoiceNumber,
          invoice_date: invoiceDate,
          taxable_amount: Number(taxableAmount || 0),
          cgst_amount: Number(cgstAmount || 0),
          sgst_amount: Number(sgstAmount || 0),
          igst_amount: Number(igstAmount || 0),
        },
        receipt_metadata: parsedBills[0]
          ? {
              extracted_amount: parsedBills[0].amount || undefined,
              extracted_vendor: parsedBills[0].merchant || undefined,
              extracted_date: undefined,
              gstin_detected: claimGst ? vendorGstin || undefined : undefined,
              confidence_score:
                parsedBills[0].confidence === 'high'
                  ? 0.9
                  : parsedBills[0].confidence === 'medium'
                    ? 0.65
                    : 0.35,
            }
          : undefined,
      };

      const created = await createExpenseEvent(payload);
      await attachReceiptIfPresent(created.id);
      navigate(`${isManagerView ? '/manager' : '/employee'}/expense-events/${created.id}`);
    } catch (submitError) {
      if (isExpenseApiError(submitError)) setError(submitError.message);
      else if (submitError instanceof Error) setError(submitError.message);
      else setError('Failed to create expense event.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminShell
      pageTitle="Add Expense"
      subtitle="Create split-aware expenses with live trip budget context."
      menuItems={isManagerView ? managerMenuItems : employeeMenuItems}
      profileName={isManagerView ? 'Approving Manager' : 'Employee'}
      profileRoleTag={isManagerView ? 'MANAGER' : 'EMPLOYEE'}
    >
      <div className="rounded-[16px] border border-[#cde4ef] bg-gradient-to-r from-[#e9f6fb] via-[#f4fbff] to-[#ffffff] px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-extrabold tracking-[0.1em] text-[#4a7a8f]">CURRENT TRIP CONTEXT</p>
            <h2 className="mt-1 text-[21px] font-extrabold text-[#0b5f77]">
              {selectedTrip ? `You are filing for ${selectedTrip.destination}` : 'Select a trip first'}
            </h2>
            <p className="mt-1 text-[13px] font-semibold text-[#5f8194]">
              {selectedTrip ? selectedTrip.period : 'Trip dates unavailable'}
            </p>
          </div>
          <div className="rounded-[12px] border border-[#d8eaf3] bg-white px-4 py-3 text-right">
            <p className="text-[11px] font-extrabold tracking-[0.09em] text-[#8fa9ba]">BUDGET REMAINING</p>
            <p className={`mt-1 text-[24px] font-extrabold ${selectedTripRemaining < 0 ? 'text-[#b54747]' : 'text-[#0b7f59]'}`}>
              {formatINR(Math.abs(selectedTripRemaining))}
              {selectedTripRemaining < 0 ? ' over' : ''}
            </p>
            <p className="text-[12px] font-semibold text-[#7995a8]">
              {formatINR(selectedTripSpent)} used of {formatINR(selectedTripBudget)}
            </p>
          </div>
        </div>
        {selectedTripBudget > 0 ? (
          <div className="mt-3">
            <div className="h-2.5 rounded-full bg-[#ddecf4]">
              <div className={`h-full rounded-full ${selectedTripRemaining < 0 ? 'bg-[#c55656]' : selectedTripBudgetPct >= 85 ? 'bg-[#ca8a04]' : 'bg-[#0a7a93]'}`} style={{ width: `${selectedTripBudgetPct}%` }} />
            </div>
            <p className="mt-1 text-[12px] font-bold text-[#5f8194]">{selectedTripBudgetPct}% consumed</p>
          </div>
        ) : null}
      </div>

      <div className="rounded-[20px] border border-[#d9e8f1] bg-white p-6">
        <h1 className="text-[32px] font-extrabold text-[#0b5f77]">Create Expense Event</h1>
        <p className="mt-1 text-[16px] font-semibold text-[#628395]">Split-aware expense creation for cross-department trips.</p>

        {error ? <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-[13px] font-semibold text-red-700">{error}</p> : null}
        {paymentLoop.step === 'pending' ? (
          <div className="mt-4 rounded-[14px] border border-[#f2d28f] bg-[#fff7df] p-4">
            <p className="text-[12px] font-extrabold tracking-[0.08em] text-[#926b17]">WAITING FOR PAYMENT CONFIRMATION</p>
            <p className="mt-1 text-[14px] font-semibold text-[#7f6320]">{paymentLoop.message}</p>
            <p className="mt-1 text-[12px] font-bold text-[#8b6a24]">Ref: {paymentLoop.transactionRef}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onCheckPaymentStatus}
                className="rounded-full border border-[#cda353] bg-white px-4 py-1.5 text-[12px] font-extrabold text-[#765619]"
              >
                Check status
              </button>
              <button
                type="button"
                onClick={onSimulateWebhook}
                className="rounded-full border border-[#cda353] bg-[#fef3c7] px-4 py-1.5 text-[12px] font-extrabold text-[#765619]"
              >
                Simulate webhook success
              </button>
            </div>
          </div>
        ) : null}
        {paymentLoop.step === 'success' ? (
          <div className="mt-4 rounded-[14px] border border-[#a7ddbf] bg-[#ebfff3] p-4">
            <p className="text-[12px] font-extrabold tracking-[0.08em] text-[#1f7f4e]">PAYMENT SUCCESSFUL</p>
            <p className="mt-1 text-[14px] font-semibold text-[#1f6f46]">{paymentLoop.message}</p>
            <p className="mt-1 text-[12px] font-bold text-[#2c7b55]">
              Status: Expense created {paymentLoop.intent.expense_event_id ? `(${paymentLoop.intent.expense_event_id.slice(0, 8)})` : ''}
            </p>
            <div className="mt-3 flex gap-2">
              {paymentLoop.intent.expense_event_id ? (
                <button
                  type="button"
                  onClick={() => navigate(`${isManagerView ? '/manager' : '/employee'}/expense-events/${paymentLoop.intent.expense_event_id}`)}
                  className="rounded-full border border-[#7fc59d] bg-white px-4 py-1.5 text-[12px] font-extrabold text-[#1f6f46]"
                >
                  View expense
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setPaymentLoop({ step: 'idle' })}
                className="rounded-full border border-[#7fc59d] bg-[#ddf6e7] px-4 py-1.5 text-[12px] font-extrabold text-[#1f6f46]"
              >
                Add another
              </button>
            </div>
          </div>
        ) : null}
        {paymentLoop.step === 'failed' ? (
          <div className="mt-4 rounded-[14px] border border-[#efb4b4] bg-[#fff1f1] p-4">
            <p className="text-[12px] font-extrabold tracking-[0.08em] text-[#a33f3f]">PAYMENT FAILED</p>
            <p className="mt-1 text-[14px] font-semibold text-[#963737]">{paymentLoop.message}</p>
            <button
              type="button"
              onClick={() => setPaymentLoop({ step: 'idle' })}
              className="mt-3 rounded-full border border-[#e29f9f] bg-white px-4 py-1.5 text-[12px] font-extrabold text-[#8f3434]"
            >
              Retry
            </button>
          </div>
        ) : null}
        <div className="mt-4 rounded-[14px] border border-[#d7eaf3] bg-[#f8fcff] p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[12px] font-extrabold tracking-[0.08em] text-[#5f8194]">BULK BILL UPLOAD & AUTO-PARSING</p>
              <p className="mt-1 text-[13px] font-semibold text-[#57788d]">Upload multiple bills. We auto-detect amount/category/merchant hints for faster filing.</p>
            </div>
            <label className="inline-flex cursor-pointer items-center justify-center rounded-full border border-[#bad7e4] bg-white px-4 py-2 text-[13px] font-bold text-[#0b6f89] hover:bg-[#eef8fc]">
              {isParsingBills ? 'Parsing bills...' : 'Upload bills'}
              <input
                type="file"
                multiple
                onChange={onBulkBillUpload}
                className="hidden"
                accept=".jpg,.jpeg,.png,.pdf,.txt,.csv,.heic,.webp"
                disabled={isParsingBills}
              />
            </label>
          </div>
          {billUploadInfo ? <p className="mt-2 text-[12px] font-semibold text-[#4d6f83]">{billUploadInfo}</p> : null}
          {parsedBills.length > 0 ? (
            <>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[12px] font-bold text-[#5b7f93]">
                  Parsed drafts: {parsedBills.length} • detected total {formatINR(parsedBillsTotal)}
                </p>
                <button
                  type="button"
                  onClick={applyAllParsedBills}
                  className="rounded-full border border-[#bad7e4] bg-white px-3 py-1 text-[12px] font-bold text-[#0b6f89] hover:bg-[#eef8fc]"
                >
                  Apply all to form
                </button>
              </div>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="border-b border-[#e6f1f7] text-left text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8ca7b8]">
                      <th className="py-2">Bill</th>
                      <th className="py-2">Category</th>
                      <th className="py-2 text-right">Amount</th>
                      <th className="py-2">Confidence</th>
                      <th className="py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedBills.map((bill) => (
                      <tr key={bill.id} className="border-b border-[#eef5f9] text-[13px] font-semibold text-[#2f4f61]">
                        <td className="py-2.5">
                          <p className="font-bold text-[#174358]">{bill.filename}</p>
                          <p className="text-[11px] text-[#7b96a7]">{bill.merchant} • source: {bill.source}</p>
                        </td>
                        <td className="py-2.5">{bill.category}</td>
                        <td className="py-2.5 text-right font-extrabold text-[#133547]">
                          {bill.amount > 0 ? formatINR(bill.amount) : '-'}
                        </td>
                        <td className="py-2.5">
                          <span className={`rounded-full px-2 py-1 text-[10px] font-extrabold tracking-[0.06em] ${
                            bill.confidence === 'high'
                              ? 'bg-[#dff5ea] text-[#166a46]'
                              : bill.confidence === 'medium'
                                ? 'bg-[#eaf3fb] text-[#215f8d]'
                                : 'bg-[#f3f4f6] text-[#6b7280]'
                          }`}>
                            {bill.confidence}
                          </span>
                        </td>
                        <td className="py-2.5">
                          <button
                            type="button"
                            onClick={() => applyParsedBill(bill)}
                            className="rounded-full border border-[#bad7e4] bg-white px-3 py-1 text-[12px] font-bold text-[#0b6f89] hover:bg-[#eef8fc]"
                          >
                            Apply
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        <form className="mt-5 space-y-4" onSubmit={onSubmit}>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-[13px] font-bold text-[#5e7f93]">
              Trip
              <select className="mt-1 h-11 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={tripId} onChange={(e) => setTripId(e.target.value)}>
                {tripOptions.map((trip) => <option key={trip.id} value={trip.id}>{trip.label}</option>)}
              </select>
            </label>
            <label className="text-[13px] font-bold text-[#5e7f93]">
              Strategy
              <select className="mt-1 h-11 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={splitStrategy} onChange={(e) => setSplitStrategy(e.target.value as SplitStrategy)}>
                {strategies.map((strategy) => <option key={strategy} value={strategy}>{strategy}</option>)}
              </select>
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-[13px] font-bold text-[#5e7f93]">Category
              <input className="mt-1 h-11 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={category} onChange={(e) => setCategory(e.target.value)} />
            </label>
            <label className="text-[13px] font-bold text-[#5e7f93]">Merchant
              <input className="mt-1 h-11 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={merchant} onChange={(e) => setMerchant(e.target.value)} />
            </label>
            <label className="text-[13px] font-bold text-[#5e7f93]">Total Amount
              <input type="number" className="mt-1 h-11 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} />
            </label>
          </div>

          <label className="text-[13px] font-bold text-[#5e7f93]">Notes
            <textarea className="mt-1 w-full rounded-[10px] border border-[#cfe0ea] px-3 py-2" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </label>

          <div className="rounded-[12px] border border-[#d5e6ef] bg-[#f8fcff] p-4">
            <button
              type="button"
              onClick={() => setShowGstDetails((prev) => !prev)}
              className="flex w-full items-center justify-between text-left"
            >
              <div>
                <p className="text-[13px] font-extrabold text-[#315e74]">Claim GST (optional)</p>
                <p className="text-[12px] font-semibold text-[#648296]">Required for tax credit.</p>
              </div>
              <span className="text-[12px] font-extrabold text-[#0b708b]">{showGstDetails ? 'Hide' : 'Add GST details'}</span>
            </button>
            {showGstDetails ? (
              <div className="mt-3 space-y-3">
                <label className="flex items-center gap-2 text-[13px] font-bold text-[#456678]">
                  <input
                    type="checkbox"
                    checked={claimGst}
                    onChange={(e) => setClaimGst(e.target.checked)}
                  />
                  GST applicable for this expense
                </label>
                {claimGst ? (
                  <>
                    <div className="grid gap-3 md:grid-cols-3">
                      <label className="text-[12px] font-bold text-[#5e7f93]">Vendor GSTIN
                        <input className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3 uppercase" value={vendorGstin} onChange={(e) => setVendorGstin(e.target.value)} maxLength={15} />
                      </label>
                      <label className="text-[12px] font-bold text-[#5e7f93]">Invoice Number
                        <input className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                      </label>
                      <label className="text-[12px] font-bold text-[#5e7f93]">Invoice Date
                        <input type="date" className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                      </label>
                    </div>
                    <div className="grid gap-3 md:grid-cols-4">
                      <label className="text-[12px] font-bold text-[#5e7f93]">Taxable Amount
                        <input type="number" className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={taxableAmount} onChange={(e) => setTaxableAmount(e.target.value)} />
                      </label>
                      <label className="text-[12px] font-bold text-[#5e7f93]">CGST
                        <input type="number" className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={cgstAmount} onChange={(e) => setCgstAmount(e.target.value)} />
                      </label>
                      <label className="text-[12px] font-bold text-[#5e7f93]">SGST
                        <input type="number" className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={sgstAmount} onChange={(e) => setSgstAmount(e.target.value)} />
                      </label>
                      <label className="text-[12px] font-bold text-[#5e7f93]">IGST
                        <input type="number" className="mt-1 h-10 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={igstAmount} onChange={(e) => setIgstAmount(e.target.value)} />
                      </label>
                    </div>
                    <p className="text-[12px] font-semibold text-[#5f8194]">Derived total GST: {formatINR(gstTotal)}</p>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="rounded-[12px] border border-[#d8e8f1] bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[13px] font-extrabold text-[#315e74]">Smart Bill Upload</p>
                <p className="text-[12px] font-semibold text-[#648296]">{receiptHint}</p>
              </div>
              <label className="inline-flex cursor-pointer items-center rounded-full border border-[#bfd8e5] bg-[#f8fcff] px-3 py-1 text-[12px] font-extrabold text-[#0c7089]">
                {receiptFile ? 'Replace file' : 'Add bill'}
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.webp,.heic"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setReceiptFile(file);
                    setReceiptUiState(file ? 'uploaded' : 'idle');
                    event.target.value = '';
                  }}
                />
              </label>
            </div>
            <p className="mt-2 text-[12px] font-semibold text-[#5f7f92]">
              Receipt status: {receiptUiState.toUpperCase()}
              {receiptFile ? ` • ${receiptFile.name}` : ''}
            </p>
          </div>

          <label className="text-[13px] font-bold text-[#5e7f93]">
            Paid By
            <select className="mt-1 h-11 w-full rounded-[10px] border border-[#cfe0ea] px-3" value={paidByEmployeeId} onChange={(e) => setPaidByEmployeeId(e.target.value)}>
              {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
            </select>
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[13px] font-extrabold text-[#5e7f93]">Split Beneficiaries</p>
              <button
                type="button"
                className="rounded-full border border-[#c6d9e5] px-3 py-1 text-[12px] font-bold text-[#0c6f88]"
                onClick={() => setSplitRows((prev) => [...prev, { employeeId: employees[0]?.id || '', amount: '0', pct: '0' }])}
              >
                Add Row
              </button>
            </div>

            <div className="space-y-2">
              {splitRows.map((row, idx) => (
                <div key={`${row.employeeId}-${idx}`} className="grid gap-2 md:grid-cols-3">
                  <select className="h-10 rounded-[10px] border border-[#cfe0ea] px-2" value={row.employeeId} onChange={(e) => {
                    const value = e.target.value;
                    setSplitRows((prev) => prev.map((item, rowIdx) => rowIdx === idx ? { ...item, employeeId: value } : item));
                  }}>
                    {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
                  </select>
                  <input
                    type="number"
                    className="h-10 rounded-[10px] border border-[#cfe0ea] px-2"
                    placeholder="Amount"
                    value={row.amount}
                    onChange={(e) => setSplitRows((prev) => prev.map((item, rowIdx) => rowIdx === idx ? { ...item, amount: e.target.value } : item))}
                  />
                  <input
                    type="number"
                    className="h-10 rounded-[10px] border border-[#cfe0ea] px-2"
                    placeholder="Percent"
                    value={row.pct}
                    onChange={(e) => setSplitRows((prev) => prev.map((item, rowIdx) => rowIdx === idx ? { ...item, pct: e.target.value } : item))}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onPayViaUpi}
              className="rounded-full bg-[#0d869f] px-6 py-2 text-[14px] font-extrabold text-white"
            >
              {isSubmitting ? 'Starting payment...' : 'Pay via UPI'}
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-full border border-[#97bfce] bg-[#eaf7fc] px-6 py-2 text-[14px] font-extrabold text-[#0b6f89]">
              {isSubmitting ? 'Creating...' : 'Create manually'}
            </button>
            <button type="button" onClick={() => navigate(isManagerView ? '/manager/history' : '/employee/dashboard')} className="rounded-full border border-[#c6d9e5] px-6 py-2 text-[14px] font-extrabold text-[#35586d]">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminShell>
  );
};

export default CreateExpenseEventPage;
